/**
 * Seitenweites Tracking: laedt Meta- und ChatGPT-Ads-Pixel einmal und meldet jeden
 * Seitenaufruf — auch bei Wechseln innerhalb der Anwendung, die ein normaler Pixel
 * nicht mitbekommt, weil die Seite dabei nicht neu geladen wird.
 *
 * Bewusst NICHT auf den internen Seiten: Dashboard, CRM, Einstellungen und die
 * Debug-Ansichten. Eigene Aufrufe der Mitarbeiter wuerden die Kennzahlen verwaessern
 * und sind als Werbe-Zielgruppe wertlos.
 *
 * Jeder Seitenaufruf geht doppelt raus, aus dem Browser und ueber die Conversions API,
 * mit derselben Event-ID. Meta fuehrt beide zusammen; genau diese Abdeckung hatte
 * Meta im Events Manager als groessten Hebel fuer niedrigere Kosten benannt.
 */
import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { loadMetaPixel, trackPixelPageView, getMetaBrowserIds, generateEventId } from '../lib/metaPixel';
import { loadOpenAiPixel } from '../lib/openaiPixel';

// Praefixe, auf denen nicht getrackt wird
const INTERNE_PFADE = [
  '/admin', '/crm', '/dashboard', '/analytics', '/settings',
  '/events-admin', '/calendar-debug', '/step2work',
];

function istIntern(pfad) {
  return INTERNE_PFADE.some(p => pfad === p || pfad.startsWith(p + '/'));
}

export default function Tracking() {
  const location = useLocation();
  const konfig = useRef(null);
  const geladen = useRef(false);
  const letzterPfad = useRef(null);

  useEffect(() => {
    const pfad = location.pathname;
    if (istIntern(pfad)) return;
    if (letzterPfad.current === pfad) return;   // gleicher Pfad, nur Parameter geaendert
    letzterPfad.current = pfad;

    const melden = () => {
      const c = konfig.current;
      if (!c) return;
      const eventId = generateEventId();
      // Beide Pixel sind voneinander unabhaengig: faellt einer aus oder ist er nicht
      // konfiguriert, muss der andere trotzdem laden. Frueher hing das ChatGPT-Pixel
      // an c.pixelId und waere ohne Meta-Pixel nie geladen worden.
      if (!geladen.current) {
        if (c.openaiPixelId) loadOpenAiPixel(c.openaiPixelId, !!c.openaiDebug);
        if (c.pixelId) loadMetaPixel(c.pixelId, eventId);
        geladen.current = true;
      } else if (c.pixelId) {
        trackPixelPageView(eventId);
      }
      if (!c.pixelId) return;
      const p = new URLSearchParams(location.search);
      const fbclid = p.get('fbclid') || '';
      const { fbc, fbp } = getMetaBrowserIds(fbclid);
      fetch('/api/pageview', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          event_id: eventId, fbc, fbp, fbclid,
          landing_url: window.location.href.split('#')[0],
        }),
      }).catch(() => {});
    };

    if (konfig.current) { melden(); return; }
    fetch('/api/lead/config')
      .then(r => r.json())
      .then(c => { konfig.current = c; melden(); })
      .catch(() => {});
  }, [location.pathname, location.search]);

  return null;
}
